<template>
  <modal @modal-close="handleClose">
    <div class="modal-wrapper">
      <CloseButton @click="handleClose" />

      <div class="modal-content">
        <div v-if="hasPhotos"
             class="overflow-y-auto"
             style="max-height: 500px; -webkit-overflow-scrolling: touch;"
        >
          <template v-for="(photo, index) in photos">
            <component
              :is="photo.resized ? 'tooltip' : 'span'"
              :key="`tooltip-${index}`"
              :label="photo.resized ? $t('assets.resize-photo-tooltip' , { width: photo.width , height: photo.height }):''"
              :multilined="true"
              position="is-right"
            >
              <DrawablePhotoV1OrV2
                  v-if="isVisiblePhoto(photo)"
                  :key="index"
                  photo-class="mr-3 mb-3"
                  :image="photo"
                  :drawing="photo.drawing"
                  :thumbnail="photo.thumbnail"
                  :width="photo.width"
                  :height="photo.height"
                  :loading="photo.loading"
                  :allow-drawing="true"
                  @change="updateDrawing(index, arguments[0])"
                  @delete="deletePhoto(index)"
                />
            </component>
          </template>
        </div>

        <label>
          <input
            ref="fileField"
            class="hidden"
            type="file"
            multiple
            :accept="allowedImageTypesInputTag"
            :disabled="loading"
            @change="handlePhotos"
          >
          <span class="btn btn-default btn-secondary w-full medium:w-auto text-center mt-4"
                :disabled="loading"
          >
            <Loader v-if="loading" />
            <template v-else>
                {{ $t('Add photo') }}
            </template>
          </span>
        </label>
      </div>

      <div class="modal-actions-outer-wrapper">
        <div class="modal-actions-inner-wrapper">
          <button
            type="button"
            class="modal-actions-secondary"
            @click.prevent="handleClose"
          >
            {{ $t('Cancel') }}
          </button>

          <button
            type="button"
            class="modal-actions-primary"
            :disabled="loading"
            @click.prevent="handleSave"
          >
            {{ $t('Save') }}
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
  import CloseButton from '@/_legacy/components/Modals/CloseButton.vue';
  import { eventBus } from '@/_legacy/eventBus';
  import Loader from '@/_legacy/components/Icons/Loader.vue';
  import { t } from '@/ui/i18n/i18n';
  import { DrawablePhotoV1OrV2 } from '@/_legacy/components/DrawablePhoto/DrawablePhotoV1OrV2';
  import Vue from 'vue';
  import { AssetsPhotoActions, getPhotoStub } from '@/services/assets-api/AssetsPhotoService';
  import * as AssetsPhotoStateService from '@/services/assets-api/AssetsPhotoStateService';
  import PhotoReader from '@/_legacy/util/PhotoReader';
  import { Tooltip } from '@/ui/components/Tooltip';
  import { allowedImageTypesInputTag } from '@/services/assets-api/assetsValidation';
  import { INSPECTION_ITEM_RESPONSE } from '@/_legacy/constants';

  const photoStubDictionary = [];

  // Note: used on Preview and as well on List response Photos addition
  // in the modal, while Inspection conduct
  export default {
    name: 'ConductInspectionModalPhotos',
    components: {
      DrawablePhotoV1OrV2,
      CloseButton,
      Loader,
      Tooltip,
    },
    props: {
      item: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      photos: [],
      loading: false,
      visiblePhotos: [],
      allowedImageTypesInputTag,
    }),
    computed: {
      hasPhotos() {
        return this.visiblePhotos && this.visiblePhotos.length > 0;
      },
    },
    watch: {
      photos: {
        handler(value) {
          if (!value) {
            return;
          }
          this.visiblePhotos = value.filter(photo => photo.action !== AssetsPhotoActions.DELETE_IMAGE);
          this.$forceUpdate();
        },
        deep: true,
        immediate: true,
      },
    },
    created() {
      if (this.item.photos && this.item.photos.length > 0) {
        this.photos = JSON.parse(JSON.stringify(this.item.photos));
      }
    },
    mounted() {
      if (this.photos && this.photos.length > 0) {
        return;
      }

      this.$refs.fileField.click();
    },
    methods: {
      isVisiblePhoto(photo) {
        return photo.action !== AssetsPhotoActions.DELETE_IMAGE;
      },
      handleClose() {
        this.$emit('close');
      },
      handleSave() {
        this.$emit('save', this.photos);
      },
      deletePhoto(index) {
        this.loading = true;
        const photoToDelete = this.photos[index];

        AssetsPhotoStateService.deletePhoto(photoToDelete);

        this.photos.splice(index, 1, photoToDelete);
        this.$set(this, 'photos', [...this.photos]);

        this.$set(this.item, 'responded', this.photos.length > 0);
        this.loading = false;
      },
      removeDrawing(index) {
        const photoToDelete = this.photos[index];

        AssetsPhotoStateService.deleteDrawing(photoToDelete);

        this.photos.splice(index, 1, photoToDelete);
        Vue.set(this, 'photos', [...this.photos]);
      },
      updateDrawing(index, drawing) {
        this.loading = true;

        if (!drawing) {
          this.removeDrawing(index);

          this.loading = false;
          return;
        }

        const photoToUpdate = this.photos[index];

        AssetsPhotoStateService.updateDrawing(photoToUpdate, drawing);

        this.photos.splice(index, 1, photoToUpdate);
        this.$set(this, 'photos', [...this.photos]);
        this.loading = false;
      },
      processUploadError(photoStub, error) {
        const errorMessage = t('Error loading image: {message}', {
          message: error,
        });
        eventBus.$emit('error', errorMessage);

        const indexPhotos = this.photos.indexOf(photoStub);
        if (indexPhotos !== -1) {
          this.photos.splice(indexPhotos, 1);
        }
      },
      async handlePhotos(event) {
        if (!event.target.files) {
          return;
        }

        this.loading = true;

        // first iteration creates the loading placeholders
        for (let i = 0; i < event.target.files.length; i++) {
          const photoStub = getPhotoStub();
          this.photos.push(photoStub);
          photoStubDictionary[i] = photoStub;
        }

        //this iteration uploads all
        for (let i = 0; i < event.target.files.length; i++) {
          const file = event.target.files[i];
          const photoStub = photoStubDictionary[i];
          if (!photoStub || !file) {
            continue;
          }

          try {
            const resizedPhoto = await PhotoReader.read(file);

            photoStub.thumbnail = resizedPhoto.thumbnail;
            photoStub.width = resizedPhoto.width;
            photoStub.height = resizedPhoto.height;
            photoStub.resized = resizedPhoto.resized;
            AssetsPhotoStateService.addImage(photoStub, resizedPhoto.photo);

            this.$delete(photoStub, 'loading');
          } catch (error) {
            this.processUploadError(photoStub, error.message);
          }
        }
        if ([INSPECTION_ITEM_RESPONSE.PHOTO, INSPECTION_ITEM_RESPONSE.SIGNATURE, INSPECTION_ITEM_RESPONSE.ANNOTATION].includes(this.item.responseType)) {
          this.$set(this.item, 'responded', this.photos.length > 0);
        }
        this.loading = false;
        event.target.value = null;
      },
    },
  };
</script>
